import {
  getInventoryItemHeads,
  getInventoryItemSubHeads,
} from '../core/_requests'
import {
  InventoryItemHead,
  InventoryItemSubHead,
} from '../core/_models'

export type NormalizedCrewScope = {
  vesselId: number | null
  companyId: number | null
  vesselName: string
  companyName: string
  vesselNameNormalized: string
  companyNameNormalized: string
}

export type ScopedInventoryLoadOptions = {
  currentUser: any
  inventoryScopeAll: boolean
  inventoryScopeCompany: boolean
  inventoryScopeVessel: boolean
  isServicesCategory?: boolean
  selectedVesselId?: number | string | null
  selectedCompanyId?: number | string | null
  selectedHeadId?: number | string | null
  requisitionVesselId?: number | string | null
  requisitionCompanyId?: number | string | null
  requisitionHeadId?: number | string | null
  selectedVesselName?: string | null
  selectedCompanyName?: string | null
  requisitionVesselName?: string | null
  requisitionCompanyName?: string | null
  requireVesselWhenCompanySelected?: boolean
  debugContext?: string
}

export type InventoryScopeSource = 'selected' | 'requisition' | 'currentUser' | 'none'

export type ScopedInventoryLoadResult<T> = {
  normalizedCrewScope: NormalizedCrewScope
  resolvedScope: NormalizedCrewScope
  selectedVesselId: number | null
  selectedCompanyId: number | null
  requisitionVesselId: number | null
  requisitionCompanyId: number | null
  selectedHeadId: number | null
  requisitionHeadId: number | null
  resolvedHeadId: number | null
  scopeSource: {
    vessel: InventoryScopeSource
    company: InventoryScopeSource
    head: InventoryScopeSource
  }
  deferred: boolean
  deferReason: string | null
  apiScope: {
    vesselId?: number
    companyId?: number
    headId?: number
  }
  rawRows: T[]
  filteredRows: T[]
  rows: T[]
  usedRawFallback: boolean
}

export const toPositiveNumber = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const normalizeScopeName = (value: any): string =>
  String(value ?? '')
    .trim()
    .toLowerCase()

const dedupeRowsByLogicalKey = <T,>(rows: T[], getKey: (row: T) => string | null): T[] => {
  const uniqueRows: T[] = []
  const seen = new Set<string>()
  ;(rows || []).forEach((row) => {
    const key = getKey(row)
    if (!key || seen.has(key)) return
    seen.add(key)
    uniqueRows.push(row)
  })
  return uniqueRows
}

export const dedupeInventoryHeadRows = (rows: InventoryItemHead[]): InventoryItemHead[] => {
  const dedupedByIdOrScope = dedupeRowsByLogicalKey(rows || [], (head) => {
    const id = toPositiveNumber(head?.id)
    if (id) return `id:${id}`

    const name = normalizeScopeName(head?.name)
    if (!name) return null

    const companyId = toPositiveNumber((head as any)?.cgaid?.id ?? (head as any)?.cgaid) ?? 0
    const vesselId = toPositiveNumber((head as any)?.vessel?.id ?? (head as any)?.vesselId) ?? 0
    return `scope:${name}|c:${companyId}|v:${vesselId}`
  })

  return dedupeRowsByLogicalKey(dedupedByIdOrScope, (head) => {
    const name = normalizeScopeName(head?.name)
    if (!name) return null
    const companyId = toPositiveNumber((head as any)?.cgaid?.id ?? (head as any)?.cgaid) ?? 0
    const vesselId = toPositiveNumber((head as any)?.vessel?.id ?? (head as any)?.vesselId) ?? 0
    return `label:${name}|c:${companyId}|v:${vesselId}`
  })
}

export const dedupeInventorySubHeadRows = (rows: InventoryItemSubHead[]): InventoryItemSubHead[] => {
  const dedupedByIdOrScope = dedupeRowsByLogicalKey(rows || [], (subHead) => {
    const id = toPositiveNumber(subHead?.id)
    if (id) return `id:${id}`

    const name = normalizeScopeName(subHead?.name)
    if (!name) return null

    const headId = toPositiveNumber(subHead?.inventoryItemHeadId ?? (subHead as any)?.inventoryItemHead?.id) ?? 0
    const companyId =
      toPositiveNumber((subHead as any)?.cgaid?.id ?? (subHead as any)?.cgaid) ??
      toPositiveNumber((subHead as any)?.inventoryItemHead?.cgaid?.id) ??
      0
    const vesselId =
      toPositiveNumber((subHead as any)?.vessel?.id ?? (subHead as any)?.vesselId) ??
      toPositiveNumber((subHead as any)?.inventoryItemHead?.vessel?.id ?? (subHead as any)?.inventoryItemHead?.vesselId) ??
      0
    return `scope:${name}|h:${headId}|c:${companyId}|v:${vesselId}`
  })

  return dedupeRowsByLogicalKey(dedupedByIdOrScope, (subHead) => {
    const name = normalizeScopeName(subHead?.name)
    if (!name) return null
    const headId = toPositiveNumber(subHead?.inventoryItemHeadId ?? (subHead as any)?.inventoryItemHead?.id) ?? 0
    const companyId =
      toPositiveNumber((subHead as any)?.cgaid?.id ?? (subHead as any)?.cgaid) ??
      toPositiveNumber((subHead as any)?.inventoryItemHead?.cgaid?.id) ??
      0
    const vesselId =
      toPositiveNumber((subHead as any)?.vessel?.id ?? (subHead as any)?.vesselId) ??
      toPositiveNumber((subHead as any)?.inventoryItemHead?.vessel?.id ?? (subHead as any)?.inventoryItemHead?.vesselId) ??
      0
    return `label:${name}|h:${headId}|c:${companyId}|v:${vesselId}`
  })
}

const getNestedValue = (obj: any, path: string): any => {
  if (!obj || !path) return undefined
  return path.split('.').reduce<any>((acc, key) => {
    if (acc === null || acc === undefined) return undefined
    return acc[key]
  }, obj)
}

const normalizeCrewScopeFromUser = (currentUser: any): NormalizedCrewScope => {
  const vesselId = [
    currentUser?.vesselId,
    currentUser?.vessel?.id,
    currentUser?.vessel?.vesselId,
    currentUser?.roleEntityId,
  ]
    .map(toPositiveNumber)
    .find((value): value is number => value !== null) ?? null

  const companyId = [
    currentUser?.companyGroupAdminId,
    currentUser?.company?.cgaid?.id,
    currentUser?.cgaid?.id,
    currentUser?.companyId,
    currentUser?.vessel?.companyGroupAdminId,
    currentUser?.vessel?.companyGroupAdmin?.id,
    currentUser?.companyGroupAdmin?.id,
    currentUser?.companyAdmin?.cgaid?.id,
  ]
    .map(toPositiveNumber)
    .find((value): value is number => value !== null) ?? null

  const vesselName =
    String(
      currentUser?.vessel?.fleet_name ??
        currentUser?.vessel?.fleetName ??
        currentUser?.vessel?.name ??
        currentUser?.vessel?.vesselName ??
        currentUser?.vesselName ??
        ''
    ).trim() || ''

  const companyName =
    String(
      currentUser?.company?.cgaid?.name ??
        currentUser?.companyGroupAdmin?.name ??
        currentUser?.vessel?.companyGroupAdmin?.name ??
        currentUser?.companyName ??
        ''
    ).trim() || ''

  return {
    vesselId,
    companyId,
    vesselName,
    companyName,
    vesselNameNormalized: normalizeScopeName(vesselName),
    companyNameNormalized: normalizeScopeName(companyName),
  }
}

const collectRowScopeIds = (row: any, paths: string[]): number[] =>
  paths
    .map((path) => toPositiveNumber(getNestedValue(row, path)))
    .filter((value): value is number => value !== null)

const collectRowScopeNames = (row: any, paths: string[]): string[] =>
  paths
    .map((path) => normalizeScopeName(getNestedValue(row, path)))
    .filter((value) => Boolean(value))

const rowMatchesCrewScope = (row: any, normalizedScope: NormalizedCrewScope): boolean => {
  const companyIdCandidates = collectRowScopeIds(row, [
    'cgaid.id',
    'cgaid',
    'companyGroupAdminId',
    'companyId',
    'company.id',
    'company.cgaid.id',
    'inventoryItemCategory.cgaid.id',
    'inventoryItemHead.cgaid.id',
    'head.cgaid.id',
  ])
  const companyNameCandidates = collectRowScopeNames(row, [
    'cgaid.name',
    'companyName',
    'company.name',
    'companyGroupAdmin.name',
    'company.cgaid.name',
    'inventoryItemCategory.cgaid.name',
    'inventoryItemHead.cgaid.name',
    'head.cgaid.name',
  ])
  const vesselIdCandidates = collectRowScopeIds(row, [
    'vesselId',
    'vessel.id',
    'vessel.vesselId',
    'roleEntityId',
    'inventoryItemCategory.vesselId',
    'inventoryItemCategory.vessel.id',
    'inventoryItemHead.vesselId',
    'inventoryItemHead.vessel.id',
    'head.vesselId',
    'head.vessel.id',
  ])
  const vesselNameCandidates = collectRowScopeNames(row, [
    'vesselName',
    'vessel.fleet_name',
    'vessel.fleetName',
    'vessel.name',
    'vessel.vesselName',
    'inventoryItemCategory.vesselName',
    'inventoryItemCategory.vessel.fleet_name',
    'inventoryItemCategory.vessel.fleetName',
    'inventoryItemHead.vesselName',
    'inventoryItemHead.vessel.fleet_name',
    'inventoryItemHead.vessel.fleetName',
    'head.vesselName',
    'head.vessel.fleet_name',
    'head.vessel.fleetName',
  ])

  const companyMatches = (() => {
    if (!normalizedScope.companyId && !normalizedScope.companyNameNormalized) return true
    const matchesById =
      normalizedScope.companyId !== null &&
      companyIdCandidates.some((rowId) => rowId === normalizedScope.companyId)
    const matchesByName =
      Boolean(normalizedScope.companyNameNormalized) &&
      companyNameCandidates.some((rowName) => rowName === normalizedScope.companyNameNormalized)
    if (companyIdCandidates.length > 0 || companyNameCandidates.length > 0) {
      return matchesById || matchesByName
    }
    return false
  })()

  const vesselMatches = (() => {
    if (!normalizedScope.vesselId && !normalizedScope.vesselNameNormalized) return true
    const matchesById =
      normalizedScope.vesselId !== null &&
      vesselIdCandidates.some((rowId) => rowId === normalizedScope.vesselId)
    const matchesByName =
      Boolean(normalizedScope.vesselNameNormalized) &&
      vesselNameCandidates.some((rowName) => rowName === normalizedScope.vesselNameNormalized)
    if (vesselIdCandidates.length > 0 || vesselNameCandidates.length > 0) {
      return matchesById || matchesByName
    }
    return true
  })()

  return companyMatches && vesselMatches
}

const resolveInventoryApiScope = (
  options: ScopedInventoryLoadOptions
) => {
  const normalizedCrewScope = normalizeCrewScopeFromUser(options.currentUser)
  const selectedVesselId = toPositiveNumber(options.selectedVesselId)
  const selectedCompanyId = toPositiveNumber(options.selectedCompanyId)
  const selectedHeadId = toPositiveNumber(options.selectedHeadId)
  const requisitionVesselId = toPositiveNumber(options.requisitionVesselId)
  const requisitionCompanyId = toPositiveNumber(options.requisitionCompanyId)
  const requisitionHeadId = toPositiveNumber(options.requisitionHeadId)

  const selectedVesselName = String(options.selectedVesselName ?? '').trim()
  const selectedCompanyName = String(options.selectedCompanyName ?? '').trim()
  const requisitionVesselName = String(options.requisitionVesselName ?? '').trim()
  const requisitionCompanyName = String(options.requisitionCompanyName ?? '').trim()

  const resolvedVesselId = selectedVesselId ?? requisitionVesselId ?? normalizedCrewScope.vesselId
  const resolvedCompanyId = selectedCompanyId ?? requisitionCompanyId ?? normalizedCrewScope.companyId
  const resolvedHeadId = selectedHeadId ?? requisitionHeadId ?? null

  const vesselSource: InventoryScopeSource =
    selectedVesselId !== null
      ? 'selected'
      : requisitionVesselId !== null
      ? 'requisition'
      : normalizedCrewScope.vesselId !== null
      ? 'currentUser'
      : 'none'
  const companySource: InventoryScopeSource =
    selectedCompanyId !== null
      ? 'selected'
      : requisitionCompanyId !== null
      ? 'requisition'
      : normalizedCrewScope.companyId !== null
      ? 'currentUser'
      : 'none'
  const headSource: InventoryScopeSource =
    selectedHeadId !== null ? 'selected' : requisitionHeadId !== null ? 'requisition' : 'none'

  const resolvedVesselName =
    (selectedVesselId !== null ? selectedVesselName : '') ||
    (requisitionVesselId !== null ? requisitionVesselName : '') ||
    normalizedCrewScope.vesselName ||
    ''
  const resolvedCompanyName =
    (selectedCompanyId !== null ? selectedCompanyName : '') ||
    (requisitionCompanyId !== null ? requisitionCompanyName : '') ||
    normalizedCrewScope.companyName ||
    ''

  const resolvedScope: NormalizedCrewScope = {
    vesselId: resolvedVesselId,
    companyId: resolvedCompanyId,
    vesselName: resolvedVesselName,
    companyName: resolvedCompanyName,
    vesselNameNormalized: normalizeScopeName(resolvedVesselName),
    companyNameNormalized: normalizeScopeName(resolvedCompanyName),
  }

  const deferred =
    Boolean(options.requireVesselWhenCompanySelected) &&
    selectedCompanyId !== null &&
    selectedVesselId === null

  return {
    normalizedCrewScope,
    resolvedScope,
    selectedVesselId,
    selectedCompanyId,
    requisitionVesselId,
    requisitionCompanyId,
    selectedHeadId,
    requisitionHeadId,
    resolvedHeadId,
    scopeSource: {
      vessel: vesselSource,
      company: companySource,
      head: headSource,
    },
    deferred,
    deferReason: deferred ? 'company_selected_waiting_for_vessel' : null,
    apiScope: {
      vesselId: resolvedVesselId ?? undefined,
      companyId: resolvedCompanyId ?? undefined,
      headId: resolvedHeadId ?? undefined,
    },
  }
}

const applyCrewScopeFilterWithFallback = <T,>(
  rows: T[],
  normalizedCrewScope: NormalizedCrewScope,
  inventoryScopeVessel: boolean
) => {
  if (!inventoryScopeVessel) {
    return {filteredRows: rows, rows, usedRawFallback: false}
  }

  const filteredRows = rows.filter((row) => rowMatchesCrewScope(row as any, normalizedCrewScope))
  return {
    filteredRows,
    rows: rows.length > 0 && filteredRows.length === 0 ? rows : filteredRows,
    usedRawFallback: rows.length > 0 && filteredRows.length === 0,
  }
}

export const loadScopedInventoryHeads = async (
  options: ScopedInventoryLoadOptions
): Promise<ScopedInventoryLoadResult<InventoryItemHead>> => {
  const scope = resolveInventoryApiScope(options)
  if (scope.deferred) {
    return {
      ...scope,
      rawRows: [],
      filteredRows: [],
      rows: [],
      usedRawFallback: false,
    }
  }

  const apiRows =
    (await getInventoryItemHeads(
      undefined,
      scope.apiScope.companyId ?? undefined
    )) || []
  const rawRows = dedupeInventoryHeadRows(apiRows)
  const {filteredRows, rows, usedRawFallback} = applyCrewScopeFilterWithFallback(
    rawRows,
    scope.resolvedScope,
    options.inventoryScopeVessel
  )

  return {
    ...scope,
    rawRows,
    filteredRows,
    rows,
    usedRawFallback,
  }
}

export const loadScopedInventorySubHeads = async (
  options: ScopedInventoryLoadOptions
): Promise<ScopedInventoryLoadResult<InventoryItemSubHead>> => {
  const scope = resolveInventoryApiScope(options)
  if (scope.deferred) {
    return {
      ...scope,
      rawRows: [],
      filteredRows: [],
      rows: [],
      usedRawFallback: false,
    }
  }

  const apiRows =
    (await getInventoryItemSubHeads(
      undefined,
      scope.apiScope.headId ?? undefined,
      scope.apiScope.companyId ?? undefined
    )) || []
  const rawRows = dedupeInventorySubHeadRows(apiRows)
  const {filteredRows, rows, usedRawFallback} = applyCrewScopeFilterWithFallback(
    rawRows,
    scope.resolvedScope,
    options.inventoryScopeVessel
  )

  return {
    ...scope,
    rawRows,
    filteredRows,
    rows,
    usedRawFallback,
  }
}
